import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import { fetchMisTableOptions } from '../../../../../../store/company/action';
import { isLenderType } from '../../../../../../utils/utils';
import { updateQueryParams } from '../../../../../../utils/searchParamUtils';

import { appendUnmatchedRows, BALANCE_SHEET_LAYOUT, CASH_FLOW_LAYOUT, PROFIT_AND_LOSS_LAYOUT } from './provisionalStatementLayout';
import { ProvisionalStatementKey, resolveStatementMetrics } from './provisionalStatementTypes';
import { useProvisionalStatementData } from './useProvisionalStatementData';
import { AuditedRowSpec, PROVISIONAL_UNIT_STORAGE_KEY } from '../../../AuditedFinancialsVisualisation/auditedStatementLayout';
import { useUnitPreference } from '../../../AuditedFinancialsVisualisation/useUnitPreference';
import { useFinancialsIngestionStatus } from '../../financialsIngestion/useFinancialsIngestionStatus';
import { FINANCIALS_FILE_TYPE } from '../../financialsIngestion/constants';

const filterParentHeader = (data: any) => ({
    ...data,
    headers: data?.headers?.filter((h: any) => h?.name !== 'Parent') ?? [],
});

export interface ProvisionalStatutorySection {
    key: ProvisionalStatementKey;
    layout: AuditedRowSpec[];
    data: any;
}

interface UseProvisionalFinancialsLogicParams {
    /** RECUR-28096 — suppresses the financials ingestion poll in PDF-report mode. */
    isPDFReport?: boolean;
}

/**
 * State, effects and derived data for the stacked Provisional Financials page (RECUR-27640),
 * colocated per CLAUDE.md so the component file stays composition + JSX.
 *
 * The four `useProvisionalStatementData` instances live here so they still run once per page, not
 * once per rendered section.
 */
export function useProvisionalFinancialsLogic({ isPDFReport = false }: UseProvisionalFinancialsLogicParams = {}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const queryParams = useParams();
    const location = useLocation();
    const dispatch = useDispatch();
    const orgId = queryParams.id ?? '';
    const isLender = isLenderType();

    const [showUpload, setShowUpload] = useState(false);

    // Options are held WITH the org they belong to, and everything visible is derived from whether
    // that org is still the current one. An effect runs after commit, so clearing state inside one
    // always paints the previous org's options (and therefore its figures) for a frame first — not
    // acceptable on a financials surface. Deriving removes that window entirely.
    const [heldOptions, setHeldOptions] = useState<{
        orgId: string;
        metricOptions: string[];
        rawOptions: any[];
        dataFormatOptions: string[];
    }>({ orgId: '', metricOptions: [], rawOptions: [], dataFormatOptions: [] });
    const optionsRequestRef = useRef(0);

    const [unit, setUnit] = useUnitPreference(PROVISIONAL_UNIT_STORAGE_KEY);

    const optionsAreCurrent = heldOptions.orgId === orgId;
    const metricOptions = optionsAreCurrent ? heldOptions.metricOptions : [];
    const rawOptions = optionsAreCurrent ? heldOptions.rawOptions : [];
    const dataFormatOptions = optionsAreCurrent ? heldOptions.dataFormatOptions : [];
    // With no org there is nothing to wait for; otherwise we are loading until this org's own
    // options are the ones being held.
    const optionsLoading = orgId ? !optionsAreCurrent : false;

    // Extracted to a callable (mirrors AuditedFinancialsVisualisation.tsx's fetchOptions) so
    // RECUR-28096's ingestion-completion handler can re-fetch options on demand, not just on
    // mount/org-change. The optionsRequestRef stale-guard is unchanged — it is what stops a
    // previous org's options landing after a switch.
    const fetchOptions = useCallback(() => {
        const requestId = ++optionsRequestRef.current;
        const isStale = () => requestId !== optionsRequestRef.current;
        dispatch(
            fetchMisTableOptions({
                orgId,
                source: 'AicaFinancials',
                type: 'mappedData',
                onSuccess: (res) => {
                    if (isStale()) return;
                    const blocks = res?.data?.data ?? [];
                    setHeldOptions({
                        orgId,
                        metricOptions: (blocks[0]?.misOptions ?? []).map((item: any) => item?.misDisplayName),
                        rawOptions: blocks[0]?.misOptions ?? [],
                        dataFormatOptions: (blocks[1]?.dataFormats ?? []).map((item: any) => item?.dataFormat),
                    });
                },
                // The saga calls this on a non-200 AND (since RECUR-27640) on a thrown request.
                // Hold empty options for this org so the page settles into its empty state rather
                // than pinning on the spinner with no error path.
                onError: () => {
                    if (isStale()) return;
                    setHeldOptions({ orgId, metricOptions: [], rawOptions: [], dataFormatOptions: [] });
                },
            }),
        );
    }, [dispatch, orgId]);

    useEffect(() => {
        if (orgId) fetchOptions();
    }, [orgId, fetchOptions]);

    const resolvedMetrics = useMemo(() => resolveStatementMetrics(metricOptions), [metricOptions]);

    const balanceSheet = useProvisionalStatementData({ orgId, metric: resolvedMetrics.balanceSheet });
    const profitAndLoss = useProvisionalStatementData({ orgId, metric: resolvedMetrics.profitAndLoss });
    const cashFlow = useProvisionalStatementData({ orgId, metric: resolvedMetrics.cashFlow });
    // Other MIS has always loaded through the raw-classification path ('1' / Unclassified).
    const otherMis = useProvisionalStatementData({
        orgId,
        metric: resolvedMetrics.otherMis,
        raw: true,
        classificationId: '1',
    });

    // RECUR-28096 — while an uploaded provisional file is still being extracted, show a loader in
    // place of "(No data available)" instead of leaving the tab looking empty/broken. No poll in
    // PDF-report mode (A-22).
    const { inProgress } = useFinancialsIngestionStatus({
        orgId,
        fileType: FINANCIALS_FILE_TYPE.PROVISIONAL,
        enabled: !isPDFReport,
        onCompleted: () => {
            // MUST be first: an org with no prior provisional data has every `resolvedMetrics.*`
            // stuck at '' (metricOptions is []), so each refetch() below would early-return at
            // useProvisionalStatementData.ts:48 with nothing to fetch. Refetching options flips
            // metric to a real value, which fires that hook's own effect and fetches the statement.
            fetchOptions();
            [balanceSheet, profitAndLoss, cashFlow, otherMis].forEach((section) => section.refetch());
        },
    });

    // Each fixed layout is extended with any classification the payload returned but the layout does
    // not name, so an unverified layout can never silently drop a figure.
    const statutorySections: ProvisionalStatutorySection[] = useMemo(() => {
        const build = (key: ProvisionalStatementKey, layout: AuditedRowSpec[], raw: any) => {
            const data = filterParentHeader(raw);
            return { key, layout: appendUnmatchedRows(layout, data), data };
        };
        return [
            build('balanceSheet', BALANCE_SHEET_LAYOUT, balanceSheet.tableData),
            build('profitAndLoss', PROFIT_AND_LOSS_LAYOUT, profitAndLoss.tableData),
            build('cashFlow', CASH_FLOW_LAYOUT, cashFlow.tableData),
        ];
    }, [balanceSheet.tableData, profitAndLoss.tableData, cashFlow.tableData]);

    const otherMisData = useMemo(() => filterParentHeader(otherMis.tableData), [otherMis.tableData]);

    // One page-level spinner covers the initial fetch, matching the audited page — not one loader per
    // statement, so the sections appear together rather than popping in one at a time.
    const isLoading = optionsLoading || balanceSheet.loading || profitAndLoss.loading || cashFlow.loading || otherMis.loading;

    const showFinancialMetrics = () => {
        updateQueryParams(searchParams, setSearchParams, { showFinancialMetrics: true }, location);
    };

    return {
        orgId,
        isLender,
        unit,
        setUnit,
        resolvedMetrics,
        statutorySections,
        otherMisData,
        rawOptions,
        dataFormatOptions,
        isLoading,
        showUpload,
        setShowUpload,
        showFinancialMetrics,
        inProgress,
    };
}
